import React, { useState, useEffect, useRef } from 'react';
import { User, Mail, Phone, Save, Loader, Camera, Lock, Eye, EyeOff } from 'lucide-react';
import adminService, { AdminProfile as AdminProfileType } from '../../services/admin/adminService';
import toast from 'react-hot-toast';

type TabType = 'personal' | 'security';

const AdminProfile: React.FC = () => {
  const [profile, setProfile] = useState<AdminProfileType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('personal');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  
  // Password visibility states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [profileForm, setProfileForm] = useState({
    full_name: '',
    mobile_number: '',
    file: null as File | null,
  });

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  useEffect(() => {
    // Debug logging on mount
    console.log('🔍 Profile Page DEBUG (Admin):', {
      admin_token: localStorage.getItem('admin_token'),
      super_admin_token: localStorage.getItem('super_admin_token'),
      authToken: localStorage.getItem('authToken'),
      userRole: localStorage.getItem('userRole'),
    });

    let attempts = 0;

    const waitForToken = () => {
      const token =
        localStorage.getItem('admin_token') ||
        localStorage.getItem('super_admin_token') ||
        localStorage.getItem('authToken');

      if (!token) {
        if (attempts < 10) {
          attempts += 1;
          console.log('⚠️ AdminProfile: token missing, retrying in 100ms... attempt:', attempts);
          return setTimeout(waitForToken, 100);
        }
        console.log('⚠️ AdminProfile: token still missing after 10 attempts. Stopping retries.');
        return;
      }

      fetchProfile();
    };

    waitForToken();
  }, []);

  const fetchProfile = async () => {
    // Check if token exists before making API call
    const token =
      localStorage.getItem('admin_token') ||
      localStorage.getItem('super_admin_token') ||
      localStorage.getItem('authToken');
    if (!token || !token.trim()) {
      console.warn('⚠️ No token found - cannot fetch profile');
      setLoading(false);
      // Redirect to login if no token
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/admin/login';
      }
      return;
    }

    try {
      setLoading(true);
      const data = await adminService.getProfile();
      setProfile(data);
      setProfileForm({
        full_name: data.name, // Backend uses 'name'
        mobile_number: data.phone, // Backend uses 'phone'
        file: null,
      });
    } catch (error: any) {
      // Handle different error types
      if (error?.isAuthError || 
          error?.message?.includes('Authentication token not found') || 
          error?.response?.status === 401 ||
          error?.response?.data?.message?.toLowerCase().includes('no token') ||
          error?.response?.data?.message?.toLowerCase().includes('unauthorized')) {
        // Token missing or invalid - redirect to login
        console.warn('⚠️ Authentication error - redirecting to login');
        if (!window.location.pathname.includes('/login')) {
          // Clear auth data
          localStorage.removeItem('authToken');
          localStorage.removeItem('isAuthenticated');
          localStorage.removeItem('userRole');
          localStorage.removeItem('userEmail');
          // Redirect immediately
          window.location.href = '/admin/login';
        }
        // Don't show error toast for auth errors
        return;
      } else {
        toast.error(error.response?.data?.message || error.message || 'Failed to fetch profile');
      }
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      await adminService.updateProfile(profileForm);
      toast.success('Profile updated successfully!');
      await fetchProfile(); // Refresh profile
      // Dispatch custom event to notify AdminLayout to refresh header image
      if (profileForm.file) {
        window.dispatchEvent(new CustomEvent('profileImageUpdated'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match!');
      return;
    }

    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    try {
      await adminService.changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      toast.success('Password changed successfully!');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to change password');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate file size (1MB max)
      if (file.size > 1024 * 1024) {
        toast.error('Image size must be less than 1MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file (JPG, PNG, or GIF)');
        return;
      }

      setProfileForm({
        ...profileForm,
        file: file,
      });

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCameraClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageUpload = async (file: File) => {
    try {
      setSaving(true);
      await adminService.updateProfile({
        full_name: profile?.name || '',
        mobile_number: profile?.phone || '',
        file: file,
      });
      toast.success('Profile image updated successfully!');
      setPreviewImage(null);
      await fetchProfile();
      // Dispatch custom event to notify AdminLayout to refresh header image
      window.dispatchEvent(new CustomEvent('profileImageUpdated'));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update profile image');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-20">
        <p className="text-secondary-600">Failed to load profile</p>
        <button 
          onClick={fetchProfile}
          className="mt-4 bg-primary-500 text-white px-6 py-3 rounded-lg font-semibold"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-1 md:mb-2">Profile Settings</h1>
          <p className="text-secondary-600 text-sm md:text-base">Manage your profile information and security settings</p>
        </div>
      </div>

      {/* Main Card with Tabs */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
          {/* Left Side - Profile Image and Info */}
          <div className="lg:col-span-4 bg-secondary-50 p-6 md:p-8 border-b lg:border-r lg:border-b-0 border-secondary-200">
            <div className="flex flex-col items-center">
              {/* Profile Picture with Camera Icon */}
              <div className="relative mb-6">
                <div className="w-32 h-32 rounded-full overflow-hidden bg-white border-4 border-white shadow-lg">
                  {previewImage || profile.profile_image ? (
                    <img
                      src={previewImage || profile.profile_image || ''}
                      alt={profile.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-white">
                      <User size={64} className="text-secondary-400" />
                    </div>
                  )}
                </div>
                {/* Camera Icon Button */}
                <button
                  onClick={handleCameraClick}
                  disabled={saving}
                  className="absolute bottom-0 right-0 bg-primary-500 hover:bg-primary-600 text-white rounded-full p-3 shadow-lg transition-all transform hover:scale-110 disabled:opacity-50"
                  title="Change profile picture"
                >
                  <Camera size={20} />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                  onChange={(e) => {
                    handleFileChange(e);
                    if (e.target.files && e.target.files[0]) {
                      handleImageUpload(e.target.files[0]);
                    }
                  }}
                  className="hidden"
                />
              </div>

              {/* Profile Info */}
              <h2 className="text-2xl font-bold text-secondary-900 mb-2 text-center">
                {profile.name}
              </h2>
              <p className="text-secondary-600 text-sm mb-3 text-center">
                {profile.email}
              </p>
              <div className="bg-primary-500 text-white px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
                Admin
              </div>

              {/* Instructions */}
              <div className="w-full bg-white rounded-lg p-4 mt-4">
                <p className="text-xs text-secondary-500 text-center">
                  Click camera icon to change profile picture<br />
                  JPG, GIF or PNG. 1MB max.
                </p>
              </div>
            </div>
          </div>

          {/* Right Side - Forms with Tabs */}
          <div className="lg:col-span-8">
            {/* Tabs */}
            <div className="border-b border-secondary-200 bg-white">
              <div className="flex overflow-x-auto scrollbar-hide">
                <button
                  onClick={() => setActiveTab('personal')}
                  className={`flex items-center justify-center space-x-2 px-4 md:px-6 py-4 font-semibold transition-all border-b-2 flex-1 whitespace-nowrap ${
                    activeTab === 'personal'
                      ? 'border-primary-500 text-primary-600 bg-primary-50'
                      : 'border-transparent text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
                  }`}
                >
                  <User size={20} />
                  <span>Personal Info</span>
                </button>
                <button
                  onClick={() => setActiveTab('security')}
                  className={`flex items-center justify-center space-x-2 px-4 md:px-6 py-4 font-semibold transition-all border-b-2 flex-1 whitespace-nowrap ${
                    activeTab === 'security'
                      ? 'border-primary-500 text-primary-600 bg-primary-50'
                      : 'border-transparent text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
                  }`}
                >
                  <Lock size={20} />
                  <span>Security</span>
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-6 md:p-8">
              {/* Personal Info Tab */}
              {activeTab === 'personal' && (
                <form onSubmit={handleProfileUpdate} className="space-y-6">
                  {/* Full Name */}
                  <div>
                    <label className="block text-sm font-semibold text-secondary-700 mb-2">
                      Full Name <span className="text-danger-500">*</span>
                    </label>
                    <div className="relative">
                      <User size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                      <input
                        type="text"
                        required
                        value={profileForm.full_name}
                        onChange={(e) => setProfileForm({...profileForm, full_name: e.target.value})}
                        className="w-full pl-11 pr-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none transition-all"
                        placeholder="Enter your full name"
                      />
                    </div>
                  </div>

                  {/* Email (Read Only) */}
                  <div>
                    <label className="block text-sm font-semibold text-secondary-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                      <input
                        type="email"
                        value={profile.email}
                        disabled
                        className="w-full pl-11 pr-4 py-3 border-2 border-secondary-200 rounded-lg bg-secondary-50 text-secondary-600 cursor-not-allowed"
                      />
                    </div>
                    <p className="text-xs text-secondary-500 mt-1">Email cannot be changed</p>
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-semibold text-secondary-700 mb-2">
                      Mobile Number <span className="text-danger-500">*</span>
                    </label>
                    <div className="relative">
                      <Phone size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                      <input
                        type="tel"
                        required
                        value={profileForm.mobile_number}
                        onChange={(e) => setProfileForm({...profileForm, mobile_number: e.target.value})}
                        className="w-full pl-11 pr-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none transition-all"
                        placeholder="Enter your mobile number"
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 sm:space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setProfileForm({
                          full_name: profile.name,
                          mobile_number: profile.phone,
                          file: null,
                        });
                        setPreviewImage(null);
                      }}
                      className="w-full sm:w-auto px-6 py-3 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="w-full sm:w-auto px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold flex items-center justify-center space-x-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save size={20} />
                      <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                    </button>
                  </div>
                </form>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <form onSubmit={handlePasswordChange} className="space-y-6">
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-secondary-900 mb-1">Change Password</h3>
                    <p className="text-sm text-secondary-600">Update your password to keep your account secure</p>
                  </div>

                  {/* Current Password */}
                  <div>
                    <label className="block text-sm font-semibold text-secondary-700 mb-2">
                      Current Password <span className="text-danger-500">*</span>
                    </label>
                    <div className="relative">
                      <Lock size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        required
                        value={passwordForm.current_password}
                        onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                        className="w-full pl-11 pr-12 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none transition-all"
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600 transition-colors"
                      >
                        {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  {/* New Password */}
                  <div>
                    <label className="block text-sm font-semibold text-secondary-700 mb-2">
                      New Password <span className="text-danger-500">*</span>
                    </label>
                    <div className="relative">
                      <Lock size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        required
                        value={passwordForm.new_password}
                        onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                        className="w-full pl-11 pr-12 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none transition-all"
                        placeholder="Enter new password (min 8 characters)"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600 transition-colors"
                      >
                        {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-sm font-semibold text-secondary-700 mb-2">
                      Confirm New Password <span className="text-danger-500">*</span>
                    </label>
                    <div className="relative">
                      <Lock size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        required
                        value={passwordForm.confirm_password}
                        onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                        className="w-full pl-11 pr-12 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none transition-all"
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600 transition-colors"
                      >
                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setPasswordForm({ current_password: '', new_password: '', confirm_password: '' })}
                      className="px-6 py-3 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold flex items-center space-x-2 transition-all"
                    >
                      <Save size={20} />
                      <span>Update Password</span>
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;

